import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Card, CardContent, Paper, useTheme, alpha, Snackbar, Alert, FormControl,
  InputLabel, Select, MenuItem, TextField, SelectChangeEvent, CircularProgress,
  Tooltip, Divider, Tab, Tabs
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon,
  Business as BusinessIcon, People as PeopleIcon, MonetizationOn as MoneyIcon,
  Science as ScienceIcon, Visibility as ViewIcon, Save as SaveIcon, Close as CloseIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const MotionCard = motion(Card);

interface Industry {
  id: string;
  name: string;
}

interface SubIndustry {
  id: string;
  name: string;
  industryId: string;
}

interface SupportEcosystemDetail {
  id?: string;
  industryId: string;
  subIndustryId?: string;
  category: 'places_infrastructure' | 'events_networks' | 'funding_incentives' | 'talent_innovation';
  name: string;
  leadText: string;
  bulletPoints: string[];
  intro: string;
  details: any;
  isActive: boolean;
  displayOrder: number;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'info' | 'warning' | 'error';
}

const SupportEcosystemManagement: React.FC = () => {
  const theme = useTheme();
  const [ecosystemData, setEcosystemData] = useState<SupportEcosystemDetail[]>([]);
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [subIndustries, setSubIndustries] = useState<SubIndustry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterIndustry, setFilterIndustry] = useState<string>('all');
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Form state
  const [formData, setFormData] = useState<SupportEcosystemDetail>({
    industryId: '',
    subIndustryId: '',
    category: 'places_infrastructure',
    name: '',
    leadText: '',
    bulletPoints: [''],
    intro: '',
    details: {},
    isActive: true,
    displayOrder: 0
  });

  useEffect(() => {
    loadData();
    loadIndustries();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('http://localhost:4001/api/support-ecosystem/all', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEcosystemData(Array.isArray(data) ? data : []);
      } else {
        setEcosystemData([]);
      }
    } catch (error) {
      console.error('Error loading support ecosystem data:', error);
      showSnackbar('Error loading data', 'error');
      setEcosystemData([]);
    } finally {
      setLoading(false);
    }
  };

  const loadIndustries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('http://localhost:4001/api/industries', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setIndustries(data.industries || []);
      }
    } catch (error) {
      console.error('Error loading industries:', error);
    }
  };

  const loadSubIndustries = async (industryId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`http://localhost:4001/api/industries/${industryId}/sub-industries`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubIndustries(data || []);
      }
    } catch (error) {
      console.error('Error loading sub-industries:', error);
    }
  };

  const showSnackbar = (message: string, severity: SnackbarState['severity']) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenForm = (item?: SupportEcosystemDetail) => {
    if (item && item.id) {
      setEditMode(true);
      setSelectedId(item.id);
      setFormData(item);
      if (item.industryId) {
        loadSubIndustries(item.industryId);
      }
    } else {
      setEditMode(false);
      setSelectedId(null);
      setFormData({
        industryId: '',
        subIndustryId: '',
        category: 'places_infrastructure',
        name: '',
        leadText: '',
        bulletPoints: [''],
        intro: '',
        details: {},
        isActive: true,
        displayOrder: 0
      });
    }
    setFormDialogOpen(true);
  };

  const handleCloseForm = () => {
    setFormDialogOpen(false);
    setEditMode(false);
    setSelectedId(null);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = editMode && selectedId
        ? `http://localhost:4001/api/support-ecosystem/${selectedId}`
        : 'http://localhost:4001/api/support-ecosystem';

      const method = editMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        showSnackbar(`Successfully ${editMode ? 'updated' : 'created'} support ecosystem entry`, 'success');
        handleCloseForm();
        loadData();
      } else {
        const errorData = await response.json();
        showSnackbar(errorData.error || 'Error saving data', 'error');
      }
    } catch (error) {
      console.error('Error saving support ecosystem data:', error);
      showSnackbar('Error saving data', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`http://localhost:4001/api/support-ecosystem/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showSnackbar('Successfully deleted support ecosystem entry', 'success');
        loadData();
      } else {
        showSnackbar('Error deleting entry', 'error');
      }
    } catch (error) {
      console.error('Error deleting support ecosystem data:', error);
      showSnackbar('Error deleting entry', 'error');
    }
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Load sub-industries when industry changes
    if (field === 'industryId' && value) {
      loadSubIndustries(value);
      setFormData(prev => ({ ...prev, subIndustryId: '' }));
    }
  };

  const handleBulletPointChange = (index: number, value: string) => {
    const newBulletPoints = [...formData.bulletPoints];
    newBulletPoints[index] = value;
    setFormData(prev => ({ ...prev, bulletPoints: newBulletPoints }));
  };

  const addBulletPoint = () => {
    setFormData(prev => ({
      ...prev,
      bulletPoints: [...prev.bulletPoints, '']
    }));
  };

  const removeBulletPoint = (index: number) => {
    const newBulletPoints = formData.bulletPoints.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, bulletPoints: newBulletPoints }));
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'places_infrastructure': return <BusinessIcon />;
      case 'events_networks': return <PeopleIcon />;
      case 'funding_incentives': return <MoneyIcon />;
      case 'talent_innovation': return <ScienceIcon />;
      default: return <BusinessIcon />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'places_infrastructure': return theme.palette.warning.main;
      case 'events_networks': return theme.palette.info.main;
      case 'funding_incentives': return theme.palette.success.main;
      case 'talent_innovation': return theme.palette.secondary.main;
      default: return theme.palette.primary.main;
    }
  };

  const getCategoryLabel = (category: string) => {
    return category.replace('_', ' & ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const filteredData = ecosystemData.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || item.category === filterCategory;
    const matchesIndustry = filterIndustry === 'all' || item.industryId === filterIndustry;
    return matchesSearch && matchesCategory && matchesIndustry;
  });

  const categoryStats = [
    {
      category: 'places_infrastructure',
      label: 'Places & Infrastructure',
      count: ecosystemData.filter(d => d.category === 'places_infrastructure').length,
      icon: <BusinessIcon />,
      color: theme.palette.warning.main
    },
    {
      category: 'events_networks',
      label: 'Events & Networks',
      count: ecosystemData.filter(d => d.category === 'events_networks').length,
      icon: <PeopleIcon />,
      color: theme.palette.info.main
    },
    {
      category: 'funding_incentives',
      label: 'Funding & Incentives',
      count: ecosystemData.filter(d => d.category === 'funding_incentives').length,
      icon: <MoneyIcon />,
      color: theme.palette.success.main
    },
    {
      category: 'talent_innovation',
      label: 'Talent & Innovation',
      count: ecosystemData.filter(d => d.category === 'talent_innovation').length,
      icon: <ScienceIcon />,
      color: theme.palette.secondary.main
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Support Ecosystem Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenForm()}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
          }}
        >
          Add New Entry
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {categoryStats.map((stat, index) => (
          <Grid item xs={12} md={3} key={index}>
            <MotionCard
              whileHover={{ y: -4 }}
              elevation={2}
              sx={{
                background: `linear-gradient(135deg, ${alpha(stat.color, 0.1)}, ${alpha(stat.color, 0.05)})`,
                border: `2px solid ${alpha(stat.color, 0.2)}`
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {stat.label}
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700 }}>
                      {stat.count}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(stat.color, 0.1),
                      color: stat.color
                    }}
                  >
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </MotionCard>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={filterCategory}
                label="Category"
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <MenuItem value="all">All Categories</MenuItem>
                <MenuItem value="places_infrastructure">Places & Infrastructure</MenuItem>
                <MenuItem value="events_networks">Events & Networks</MenuItem>
                <MenuItem value="funding_incentives">Funding & Incentives</MenuItem>
                <MenuItem value="talent_innovation">Talent & Innovation</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Industry</InputLabel>
              <Select
                value={filterIndustry}
                label="Industry"
                onChange={(e) => setFilterIndustry(e.target.value)}
              >
                <MenuItem value="all">All Industries</MenuItem>
                {industries.map(industry => (
                  <MenuItem key={industry.id} value={industry.id}>{industry.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Industry</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Order</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No support ecosystem entries found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            mr: 2,
                            p: 1,
                            borderRadius: 1,
                            bgcolor: alpha(getCategoryColor(item.category), 0.1),
                            color: getCategoryColor(item.category),
                            display: 'flex'
                          }}
                        >
                          {getCategoryIcon(item.category)}
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getCategoryLabel(item.category)}
                        size="small"
                        sx={{
                          bgcolor: alpha(getCategoryColor(item.category), 0.1),
                          color: getCategoryColor(item.category),
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {industries.find(i => i.id === item.industryId)?.name || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={item.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{item.displayOrder}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenForm(item)}
                          sx={{ mr: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => item.id && handleDelete(item.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Form Dialog */}
      <Dialog
        open={formDialogOpen}
        onClose={handleCloseForm}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editMode ? 'Edit Support Ecosystem Entry' : 'Add New Support Ecosystem Entry'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Industry</InputLabel>
                <Select
                  value={formData.industryId}
                  label="Industry"
                  onChange={(e) => handleFormChange('industryId', e.target.value)}
                >
                  {industries.map(industry => (
                    <MenuItem key={industry.id} value={industry.id}>{industry.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Sub-Industry (Optional)</InputLabel>
                <Select
                  value={formData.subIndustryId || ''}
                  label="Sub-Industry (Optional)"
                  onChange={(e) => handleFormChange('subIndustryId', e.target.value)}
                  disabled={!formData.industryId}
                >
                  <MenuItem value="">None</MenuItem>
                  {subIndustries.map(sub => (
                    <MenuItem key={sub.id} value={sub.id}>{sub.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => handleFormChange('category', e.target.value)}
                >
                  <MenuItem value="places_infrastructure">Places & Infrastructure</MenuItem>
                  <MenuItem value="events_networks">Events & Networks</MenuItem>
                  <MenuItem value="funding_incentives">Funding & Incentives</MenuItem>
                  <MenuItem value="talent_innovation">Talent & Innovation</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Display Order"
                type="number"
                value={formData.displayOrder}
                onChange={(e) => handleFormChange('displayOrder', parseInt(e.target.value))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Lead Text"
                multiline
                rows={2}
                value={formData.leadText}
                onChange={(e) => handleFormChange('leadText', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Introduction"
                multiline
                rows={4}
                value={formData.intro}
                onChange={(e) => handleFormChange('intro', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Bullet Points
              </Typography>
              {formData.bulletPoints.map((point, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={point}
                    onChange={(e) => handleBulletPointChange(index, e.target.value)}
                    placeholder={`Bullet point ${index + 1}`}
                  />
                  {formData.bulletPoints.length > 1 && (
                    <IconButton size="small" onClick={() => removeBulletPoint(index)}>
                      <CloseIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Button size="small" onClick={addBulletPoint} startIcon={<AddIcon />}>
                Add Bullet Point
              </Button>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.isActive ? 'active' : 'inactive'}
                  label="Status"
                  onChange={(e) => handleFormChange('isActive', e.target.value === 'active')}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Note: Detailed content (tables, lists, etc.) can be managed through the JSON details field in future versions.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseForm}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            startIcon={<SaveIcon />}
            disabled={!formData.name || !formData.leadText || !formData.industryId}
          >
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SupportEcosystemManagement;
